/**
 * RemoteCursors Component
 * Displays other users' cursor positions on the timeline
 */

import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Individual cursor component
const RemoteCursor = memo(({ user, position, timelineWidth, zoom }) => {
  if (!position || position.x === undefined) return null;
  
  // Calculate pixel position from time position
  const pixelX = position.time !== undefined 
    ? (position.time / 60) * timelineWidth * zoom // Convert time to pixels
    : position.x;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.15 }}
      className="absolute pointer-events-none z-50"
      style={{
        left: `${pixelX}px`,
        top: `${position.y || 0}px`,
        transform: 'translate(-2px, -2px)',
      }}
      data-testid={`remote-cursor-${user.id}`}
    >
      {/* Cursor pointer */}
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="drop-shadow-lg"
      >
        <path
          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.92L5.94 2.36a.5.5 0 0 0-.44.85z"
          fill={user.color || '#6366f1'}
          stroke="white"
          strokeWidth="1.5"
        />
      </svg>
      
      {/* User label */}
      <div
        className="absolute left-4 top-4 px-2 py-0.5 rounded text-xs font-medium text-white whitespace-nowrap shadow-lg"
        style={{ backgroundColor: user.color || '#6366f1' }}
      >
        {user.name}
      </div>
    </motion.div>
  );
});

RemoteCursor.displayName = 'RemoteCursor';

// Main RemoteCursors container
export const RemoteCursors = memo(({ 
  cursors, // Map of userId -> { position, user }
  users,   // Array of user objects with id, name, color
  currentUserId,
  timelineWidth = 1000,
  zoom = 1,
}) => {
  // Filter out current user and users without cursor positions
  const remoteCursors = Object.entries(cursors || {})
    .filter(([userId]) => userId !== currentUserId)
    .map(([userId, data]) => {
      const user = users.find(u => u.id === userId) || { 
        id: userId, 
        name: 'Unknown', 
        color: '#6366f1' 
      };
      return { userId, user, position: data };
    })
    .filter(({ position }) => position !== null);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="remote-cursors-container">
      <AnimatePresence>
        {remoteCursors.map(({ userId, user, position }) => (
          <RemoteCursor
            key={userId}
            user={user}
            position={position}
            timelineWidth={timelineWidth}
            zoom={zoom}
          />
        ))}
      </AnimatePresence>
    </div>
  );
});

RemoteCursors.displayName = 'RemoteCursors';

export default RemoteCursors;
